import * as React from 'react';

import { cn } from '@/lib/utils';

type AlertVariant = 'success' | 'error' | 'warning' | 'info' | 'default';

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
}

const getAlertVariantClasses = (variant: AlertVariant = 'default'): string => {
  const baseClasses =
    'relative w-full rounded-lg p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground';

  const variantClasses = {
    default: 'bg-background text-foreground',
    error: 'border border-red-500 text-red-500 [&>svg]:text-red-500 bg-red-200',
    warning:
      'border border-yellow-500 text-yellow-500 [&>svg]:text-yellow-500 bg-yellow-50',
    info: 'border border-blue-500 text-blue-500 [&>svg]:text-blue-500 bg-blue-200',
    success:
      'border border-green-500 text-green-500 [&>svg]:text-green-500 bg-green-200',
  };

  return cn(baseClasses, variantClasses[variant]);
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(getAlertVariantClasses(variant), className)}
      {...props}
    />
  )
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 leading-none text-large', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-body', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };

